
<template>
  <div>
    <h1>Earns Methoden</h1>
    <p>
      Maßnahmen können mit der überschlägigen oder der genormten / wissenschaftlichen
      Methode berechnet werden. Die überschlägigen Methoden benötigen weniger Daten
      und sind somit schneller zu untersuchen. Die genormten / wissenschaftlichen Methoden
      bieten auf der anderen Seite eine zuverlässige Berechnung welche belastbar sind.
    </p>
    <p>
      Um eine Maßnahme abspeichern zu können, sind in alle technischen
      Pflichtfelder valide Werte einzutragen. Zusätzlich können optional die
      finanziellen Parameter eingetragen werden.
    </p>

    <h1>Photovoltaik</h1>
    <p>
      Diese Methode ermittelt den Ertrag einer Photovoltaikanlage direkt aus dem
      Standort und der Ausrichtung der Module. Anders als bei „Sonstige
      erneuerbare Energien“ muss die Stromerzeugung also nicht extern berechnet
      werden: EARNS fragt sie über die PVGIS-Schnittstelle der Europäischen
      Kommission ab.
    </p>

    <v-expansion-panels accordion class="mt-5">
      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Schritte vor der Berechnung</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <ol>
            <li>
              <b>Standort bestimmen</b>
              <p>
                Breiten- und Längengrad des Gebäudes in Dezimalgrad eintragen,
                zum Beispiel 48.1372 und 11.5755 für München. Die Werte lassen
                sich aus jedem Kartendienst ablesen.
              </p>
            </li>
            <li>
              <b>Anlage beschreiben</b>
              <p>
                Nennleistung in kWp, Neigung und Ausrichtung der Modulfläche
                sowie den Modultyp angeben. Die Ausrichtung folgt der
                Kompassrose: Süd entspricht 180°.
              </p>
            </li>
            <li>
              <b>Ertrag über PVGIS berechnen</b>
              <p>
                Der ermittelte spezifische Ertrag in kWh je kWp und Jahr wird in
                das Feld übernommen und gespeichert. Er kann jederzeit durch
                einen eigenen Simulationswert, etwa aus PV*SOL, ersetzt werden.
              </p>
            </li>
            <li>
              <b>Eigenverbrauchsanteil abschätzen</b>
              <p>
                Nur der Eigenverbrauch innerhalb der Bilanzgrenze reduziert
                Energiekosten, CO2 und Primärenergie. Für eingespeiste Mengen
                wird die Vergütung angerechnet.
              </p>
            </li>
            <li>
              <b>Vergütungstarif prüfen</b>
              <p>
                Der Vergütungstarif für die Einspeisung kann in den
                <router-link :to="{ name: 'ProjectDetails' }">
                  Projektdetails
                </router-link>
                angepasst werden.
              </p>
            </li>
            <li>
              <b>
                Kosten für die gewählte Systemlösung ermitteln, unter
                Berücksichtigung aller relevanten Faktoren wie Aufwand für
                Demontage, Umbau, Einbringung, Anschlussarbeiten,
                Inbetriebnahme, etc.
              </b>
            </li>
          </ol>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Berechnungsgrundlage und Daten</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text eager>
          <v-container>
            <h2>Ertragsermittlung</h2>
            <p>
              Der Ertrag stammt aus dem Photovoltaic Geographical Information
              System (PVGIS) des Joint Research Centre der Europäischen
              Kommission. PVGIS simuliert die Anlage stündlich auf Basis
              gemessener Einstrahlungsdaten (Standard: SARAH-3) und
              berücksichtigt dabei Spektrum, Reflexion an der Modulscheibe,
              Modultemperatur und schwache Einstrahlung.
            </p>
            <p>
              Abgefragt wird standardmäßig der Zeitraum 2014 bis 2024. Die
              gelieferte Energiemenge ist die Summe über den gesamten Zeitraum;
              EARNS teilt sie durch die Anzahl der Jahre und erhält so einen
              über elf Wetterjahre gemittelten Jahresertrag. Damit wirken sich
              einzelne besonders sonnige oder trübe Jahre nicht auf das Ergebnis
              aus.
            </p>

            <h2>Von der Erzeugung zur Bilanz</h2>
            <p>
              Die Jahreserzeugung ergibt sich aus
              <em>spezifischer Ertrag × Nennleistung × Anzahl</em>. Sie wird
              anschließend aufgeteilt:
            </p>
            <ul>
              <li>
                Der <b>Eigenverbrauch</b> ersetzt bezogenen Netzstrom und senkt
                dadurch Energiekosten, CO2-Ausstoß und Primärenergiebedarf.
              </li>
              <li>
                Die <b>Einspeisung</b> wird mit dem Vergütungstarif des
                Netzbetreibers verrechnet.
              </li>
            </ul>

            <h2>Abregelung</h2>
            <p>
              Ist die Einspeisung nicht aktiviert, verfällt der nicht selbst
              verbrauchte Strom. Er wird weder vergütet noch in der Bilanz
              geführt. Das entspricht einer abgeregelten Anlage. Soll der
              gesamte Ertrag genutzt werden, setzen Sie den Eigenverbrauchsanteil
              auf 100 %.
            </p>

            <h2>CO2-Gutschrift für die Einspeisung</h2>
            <p>
              Eingespeiste Energie verlässt die Bilanzgrenze des Unternehmens.
              Nach der in EARNS verwendeten Systematik werden CO2- und
              Primärenergieeinsparung deshalb <b>nur für den Eigenverbrauch</b>
              angerechnet. Die Option „CO2-Gutschrift für Einspeisung“ ist
              daher standardmäßig ausgeschaltet.
            </p>
            <p>
              Wird sie aktiviert, so wird der eingespeiste Strom mit dem
              CO2-Faktor des Netzstroms gutgeschrieben. Das bildet die
              tatsächliche Verdrängung von Kraftwerksstrom ab und ist zum
              Beispiel für eine Treibhausgasbilanz nach Scope 2 sinnvoll. Der
              Ausweis weicht dann bewusst von der reinen Bilanzgrenzenbetrachtung
              ab und sollte im Bericht kenntlich gemacht werden.
            </p>
            <p>
              Die Einstellung gilt für das <b>gesamte Projekt</b> und steht in den
              <router-link :to="{ name: 'ProjectDetails' }">
                Projektdetails
              </router-link>
              — nicht je Anlage. DIN EN ISO 52000-1 behandelt die Bewertung der
              Einspeisung als Festlegung der Bilanzierung (dort der Faktor
              <i>k</i><sub>exp</sub>), nicht als Eigenschaft der einzelnen Anlage.
              Andernfalls könnten zwei Photovoltaikmaßnahmen im selben Projekt
              unterschiedlich bilanziert werden und die Projektsumme beides mischen.
            </p>
          </v-container>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Eingabe der Referenz- und Geplanten-Anlage</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <table>
            <caption>Tabelle: Technische Parameter</caption>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Beschreibung</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Vorhanden (Referenz)</td>
                <td>Auswahl ob Referenzanlage vorhanden</td>
              </tr>
              <tr>
                <td>Breiten- und Längengrad</td>
                <td>Standort der Anlage in Dezimalgrad</td>
              </tr>
              <tr>
                <td>Anzahl</td>
                <td>Anzahl baugleicher Anlagen mit gleicher Ausrichtung</td>
              </tr>
              <tr>
                <td>Nennleistung</td>
                <td>Spitzenleistung einer Anlage in kWp</td>
              </tr>
              <tr>
                <td>Neigung</td>
                <td>Neigung gegen die Waagerechte in Grad, 0° liegt flach</td>
              </tr>
              <tr>
                <td>Ausrichtung</td>
                <td>Azimut: 0° = Nord, 90° = Ost, 180° = Süd, 270° = West</td>
              </tr>
              <tr>
                <td>Modultyp</td>
                <td>Zelltechnologie und Montageart</td>
              </tr>
              <tr>
                <td>Systemverluste</td>
                <td>
                  Verluste in Wechselrichter, Verkabelung und durch
                  Verschmutzung. Üblich sind 14 %.
                </td>
              </tr>
              <tr>
                <td>Spezifischer Ertrag</td>
                <td>Jahresertrag je kWp, über PVGIS ermittelt oder manuell</td>
              </tr>
              <tr>
                <td>Eigenverbrauchsanteil</td>
                <td>Anteil des Ertrags, der im Objekt verbraucht wird</td>
              </tr>
              <tr>
                <td>Einspeisung ins Netz</td>
                <td>
                  Auswahl ob der Überschuss vergütet eingespeist wird.
                  Ohne Einspeisung verfällt er.
                </td>
              </tr>
              <tr>
                <td>CO2-Gutschrift für Einspeisung</td>
                <td>
                  Auswahl ob der eingespeiste Strom in die CO2- und
                  Primärenergiebilanz eingeht. <b>Projektweite Einstellung</b> in
                  den Projektdetails, nicht je Anlage.
                </td>
              </tr>
            </tbody>
          </table>

          <table>
            <caption>Tabelle: Finanzielle Parameter</caption>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Beschreibung</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Demontagekosten (Referenz)</td>
                <td>
                  Gesamtkosten der Demontagekosten inkl. Arbeitszeit,
                  Maschinenkosten und Entsorgungskosten.
                </td>
              </tr>
              <tr>
                <td>Kosten Neuanlage (Geplant)</td>
                <td>Gesamte Anschaffungskosten bis hin zur Inbetriebnahme.</td>
              </tr>
              <tr>
                <td>Betriebs- und Instandhaltungskosten</td>
                <td>
                  Jährliche Kosten für Betriebsmaterial und
                  Instandhaltungsarbeiten (ohne Energiekosten).
                </td>
              </tr>
            </tbody>
          </table>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Beispiel</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text eager>
          <v-container>
            <h2>Neubau einer Aufdachanlage</h2>
            <p>
              Eine 100 kWp Anlage in München, nach Süden ausgerichtet und um 30°
              geneigt, ohne bestehende Referenzanlage.
            </p>

            <table>
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Wert</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Vorhanden (Referenz)</td>
                  <td>Nein, es gibt noch keine Anlage.</td>
                </tr>
                <tr>
                  <td>Breitengrad / Längengrad</td>
                  <td>48.1372 / 11.5755</td>
                </tr>
                <tr>
                  <td>Anzahl</td>
                  <td>1</td>
                </tr>
                <tr>
                  <td>Nennleistung</td>
                  <td>100 kWp</td>
                </tr>
                <tr>
                  <td>Neigung / Ausrichtung</td>
                  <td>30° / Süd (180°)</td>
                </tr>
                <tr>
                  <td>Spezifischer Ertrag</td>
                  <td>rund 1.230 kWh/kWp·a (über PVGIS ermittelt)</td>
                </tr>
                <tr>
                  <td>Erzeugung</td>
                  <td>rund 123.000 kWh/a</td>
                </tr>
                <tr>
                  <td>Eigenverbrauchsanteil</td>
                  <td>40 %, also rund 49.200 kWh/a</td>
                </tr>
                <tr>
                  <td>Einspeisung ins Netz</td>
                  <td>Ja, rund 73.800 kWh/a</td>
                </tr>
              </tbody>
            </table>

            <p>
              Für komplexere Erzeugersysteme sprechen Sie uns gerne an.
            </p>
          </v-container>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Quellen</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <h2>Quellen</h2>
          <ul>
            <li>
              PVGIS – Photovoltaic Geographical Information System,
              Joint Research Centre (JRC) der Europäischen Kommission
              (https://photovoltaic-geographic-information-system.ec.europa.eu/api/)
            </li>
            <li>
              Huld, T. et al.: Leistungsmodell für Photovoltaikmodule,
              Grundlage der PVGIS-Simulation
            </li>
            <li>
              SARAH-3, Satellitengestützter Einstrahlungsdatensatz des
              EUMETSAT CM SAF
            </li>
            <li>
              Umweltbundesamt: „Erneuerbare Energien in Deutschland"
            </li>
          </ul>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

  </div>
</template>

<script setup lang="ts">
defineOptions({
  name: "PVHelp"
});
</script>

<style scoped>
</style>
